/**
 * DeleteConfirmModal Component
 * 
 * Confirmation dialog for deleting an opportunity.
 * Requires user to confirm the action.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, Modal, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon } from '../../../shared/tokens/mobile';

interface DeleteConfirmModalProps {
  visible: boolean;
  opportunityName: string;
  onCancel: () => void;
  onConfirm: () => void;
  deleting?: boolean;
}

export function DeleteConfirmModal({
  visible,
  opportunityName,
  onCancel,
  onConfirm,
  deleting = false,
}: DeleteConfirmModalProps) {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <Pressable style={styles.backdrop} onPress={deleting ? undefined : onCancel}>
        <View style={styles.modal} onStartShouldSetResponder={() => true}>
          <View style={styles.iconContainer}>
            <Ionicons name="warning-outline" size={icon.xxl} color={color.error} />
          </View>
          
          <Text style={styles.title}>Delete Opportunity?</Text>
          
          <Text style={styles.message}>
            Are you sure you want to permanently delete{' '}
            <Text style={styles.opportunityName}>{opportunityName}</Text>?
          </Text>
          
          <Text style={styles.warning}>
            This action cannot be undone. All associated data including ideas, comments, and parcel links will be removed.
          </Text>
          
          <View style={styles.buttons}>
            <Pressable
              onPress={onCancel}
              disabled={deleting}
              style={({ pressed }) => [
                styles.button,
                styles.cancelButton,
                pressed && styles.buttonPressed,
              ]}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            
            <Pressable
              onPress={onConfirm}
              disabled={deleting}
              style={({ pressed }) => [
                styles.button,
                styles.deleteButton,
                pressed && styles.deleteButtonPressed,
                deleting && styles.buttonDisabled,
              ]}
            >
              {deleting ? (
                <ActivityIndicator size="small" color={color.surface} />
              ) : (
                <>
                  <Ionicons name="trash-outline" size={icon.sm} color={color.surface} />
                  <Text style={styles.deleteButtonText}>Delete</Text>
                </>
              )}
            </Pressable>
          </View>
        </View>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: space.lg,
  },
  modal: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: color.errorLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space.md,
  },
  title: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
    marginBottom: space.sm,
  },
  message: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
    textAlign: 'center',
    lineHeight: text.body.lineHeight,
  },
  opportunityName: {
    fontWeight: '600',
    color: color.textPrimary,
  },
  warning: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    textAlign: 'center',
    marginTop: space.sm,
    lineHeight: text.caption.lineHeight * 1.2,
  },
  buttons: {
    flexDirection: 'row',
    gap: space.sm,
    marginTop: space.lg,
    width: '100%',
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: space.sm + space.xs,
    borderRadius: radius.md,
    gap: space.xs,
  },
  buttonPressed: {
    opacity: 0.8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  cancelButton: {
    backgroundColor: color.backgroundSecondary,
    borderWidth: 1,
    borderColor: color.border,
  },
  cancelButtonText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  deleteButton: {
    backgroundColor: color.error,
  },
  deleteButtonPressed: {
    opacity: 0.85,
  },
  deleteButtonText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.surface,
  },
});

